<script lang="ts" setup>
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faFilters } from "@fortawesome/pro-solid-svg-icons";
import { industries } from "~/services/CoreSignalService";
import type { BrandFetchSearch } from "~/services/BrandFetchService";
import type { PartnerTenant } from "~/models/User";

interface BrandSelection {
    id: string;
    name: string;
    icon: string;
    website: string;
    provider: string;
}

const isOpen = defineModel<boolean>("open", {
    default: false,
});

const props = withDefaults(
    defineProps<{
        partner?: boolean;
        switchTheAccount?: () => void;
        createAccountModal?: () => void;
        switchAccountModal?: () => void;
    }>(),
    {},
);

const emit = defineEmits<{
    (e: "switch-account", account_id: number): void;
    (e: "partner-tenant-created", tenant: PartnerTenant): void;
}>();

const { user: auth, refreshIdentity } = useAuth();
const api = useApi();
const router = useRouter();

const accountExisted = ref(false);
const submitting = ref(false);
const showBrands = ref(false);
const loadingBrands = ref(false);
const brands = ref<BrandFetchSearch[]>([]);
const selected = ref<BrandSelection | null>(null);
const mouseIn = ref(false);
const isFilterOpen = ref(false);
const selectedCountry = ref(["Netherlands"]);
const selectedIndustry = ref([
    "Human Resources Services",
    "Staffing & Recruiting",
]);

const user = reactive({
    email: auth.value?.email,
    account_id: auth.value?.account.id,
    account: "",
    is_super_admin: auth.value?.is_super_admin,
});

async function createAccount() {
    try {
        accountExisted.value = false;
        submitting.value = true;

        if (props.partner) {
            const data = await api.partners.tenants.create({
                name: user.account,
                identifier: selected.value?.id,
                provider: selected.value?.provider,
            });

            useToast().add({ title: "Account created!" });
            isOpen.value = false;
            submitting.value = false;

            emit("partner-tenant-created", data.data);
        } else {
            await api.account.create({
                name: user.account,
                identifier: selected.value?.id,
                provider: selected.value?.provider,
            });
            await refreshIdentity();
            props.createAccountModal?.();

            useToast().add({ title: "Account created!" });

            await refreshNuxtData();
            submitting.value = false;
            router.push("/?tab=all");
        }
    } catch {
        accountExisted.value = true;
        submitting.value = false;
    }
}

function onFocusBrandInput() {
    if (brands.value.length) {
        showBrands.value = true;
    }
}

function onBlurBrandInput() {
    if (!mouseIn.value) {
        showBrands.value = false;
    }
}

async function fetchBrands(name: string) {
    if (!name) {
        showBrands.value = false;
        return;
    }

    if (name.length <= 2) return;

    showBrands.value = true;
    loadingBrands.value = true;

    const res = await api.brandFetch.search(name);
    if (res?.data) {
        brands.value = res.data;
    }

    loadingBrands.value = false;
}

const debouncedFetchBrands = useDebounce(fetchBrands, 500);

watch(isOpen, (isOpened: boolean) => {
    if (!isOpened) {
        accountExisted.value = false;
        user.account = "";
        selected.value = null;
        loadingBrands.value = false;
        showBrands.value = false;
    }
});

const { data } = api.localization.countries({
    query: { per_page: 300 },
});

const countries = computed(() => {
    return data.value?.data?.data.map((country: any) => country.name) ?? [];
});

function onChangeBrand(e: Event) {
    const value = (e.target as HTMLInputElement).value;
    selected.value = null;
    debouncedFetchBrands(value);
}

function onBrandSelected(data: any) {
    selected.value = {
        id: data.brandId || data.id,
        name: data.name,
        icon: data.icon || data.logo_url || "",
        website: data.domain || data.website || "",
        provider: data.brandId ? "brandfetch" : "coresignal",
    };
    user.account = data.name;
    showBrands.value = false;
}

function clearFilter() {
    selectedCountry.value = [];
    selectedIndustry.value = [];
}

function applyFilter() {
    isFilterOpen.value = false;
    debouncedFetchBrands(user.account);
}

function onAccountSwitch() {
    props?.switchTheAccount?.();
    emit("switch-account", user.account_id);
}

function onBack() {
    props?.createAccountModal?.();
    props?.switchAccountModal?.();
}
</script>

<template>
    <UModal v-model="isOpen">
        <UForm :state="user" @submit="createAccount">
            <UCard
                :ui="{
                    ring: '',
                    divide: 'divide-y divide-gray-100 dark:divide-gray-800',
                }"
            >
                <template #header>
                    <div class="items-center text-center justify-between">
                        <h1 class="text-xl font-medium font-display">
                            New Account
                        </h1>
                    </div>
                </template>
                <div class="flex flex-col gap-3">
                    <div class="flex gap-2 items-end">
                        <UFormGroup
                            :error="accountExisted"
                            class="flex-1"
                            label="Company"
                        >
                            <UInput
                                v-model="user.account"
                                :icon="
                                    !selected
                                        ? 'i-heroicons-building-office'
                                        : null
                                "
                                autocomplete="company"
                                placeholder="HireData"
                                size="md"
                                @blur="onBlurBrandInput"
                                @focus="onFocusBrandInput"
                                @input="onChangeBrand"
                            >
                                <template #leading>
                                    <img
                                        v-if="selected"
                                        :src="selected.icon"
                                        alt="Default avatar"
                                        class="w-5 h-5 rounded"
                                        @error="
                                            $event.target.src =
                                                '/assets/logo/svg/linkedin_empty_company_logo.svg'
                                        "
                                    />
                                </template>
                            </UInput>
                            <AppBrandList
                                :items="brands"
                                :loading="loadingBrands"
                                :selected="selected"
                                :show="showBrands"
                                @mouseenter="mouseIn = true"
                                @mouseleave="mouseIn = false"
                                @select="onBrandSelected"
                            />
                        </UFormGroup>
                        <div class="items-end justify-end">
                            <UPopover
                                v-model:open="isFilterOpen"
                                :popper="{ placement: 'right' }"
                                :ui="{
                                    background: '!overflow-visible',
                                    base: 'focus:outline-none',
                                }"
                            >
                                <UButton
                                    class="p-[11px]"
                                    color="white"
                                    size="lg"
                                    square
                                >
                                    <FontAwesomeIcon
                                        :icon="faFilters"
                                        class="opacity-50 text-gray-600"
                                    />
                                </UButton>

                                <template #panel>
                                    <div class="p-4 w-72">
                                        <div class="flex flex-col gap-2">
                                            <h3
                                                class="font-medium text-lg flex justify-between items-end"
                                            >
                                                <span>Filters</span>
                                                <UButton
                                                    color="white"
                                                    size="xs"
                                                    @click="clearFilter"
                                                >
                                                    Clear Filters
                                                </UButton>
                                            </h3>
                                            <UFormGroup label="Industry">
                                                <USelectMenu
                                                    v-model="selectedIndustry"
                                                    :options="industries"
                                                    multiple
                                                    searchable
                                                />
                                            </UFormGroup>
                                            <UFormGroup label="Country">
                                                <USelectMenu
                                                    v-model="selectedCountry"
                                                    :options="countries"
                                                    multiple
                                                    searchable
                                                />
                                            </UFormGroup>
                                        </div>
                                        <UButton
                                            class="mt-4"
                                            color="primary"
                                            size="xs"
                                            @click="applyFilter"
                                        >
                                            Apply
                                        </UButton>
                                    </div>
                                </template>
                            </UPopover>
                        </div>
                    </div>
                    <p
                        v-if="accountExisted"
                        class="mt-2 text-red-500 dark:text-red-400 text-sm"
                    >
                        This account already exists. Do you want to
                        <ULink
                            class="text-blue-600 hover:underline"
                            @click="onAccountSwitch"
                        >
                            switch to this account?
                        </ULink>
                    </p>
                </div>
                <template #footer>
                    <div class="grid gap-3">
                        <UButton
                            :disabled="submitting || user.account === ''"
                            :loading="submitting"
                            block
                            type="submit"
                        >
                            Create Account
                        </UButton>
                        <UButton block color="gray" @click="onBack">
                            <FontAwesomeIcon :icon="faArrowLeft" class="mr-1" />
                            <span>Back to Account Switch</span>
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </UModal>
</template>
